import React from 'react'
import { useCallback, useState, useRef, useEffect, useMemo, RefObject } from 'react'
import { BackButton, LoginButton, NavBar } from '../../../components/layout/NavBar'
import { Services } from '@/services/services'
import { useNavigate, useParams } from 'react-router-dom'

import './Dashboard.module.css'
import { callMeBtn } from '@/components/Elements/CallMeBtn'
import { Translates } from '@/services/repository/i18n'
import { CurrentSession } from '@/services/repository/session'
import { LoggedInLayout } from '@/components/layout/LoggedInLayout'
import { MainLayout, MainPageLayout } from '@/components/layout/MainLayout'
import { useLogin } from '@/context/login'
import { Machine } from '@/services/model/machine'
import { useModal } from '@/components/modal/Modal'
import { MachinePopupDocBody } from '@/popups/docs/docs'
import { MachinePopupQRCodeBody } from '@/popups/qrcode/qrcode'
import { MachineFailureFormData, NewMachineFailureForm } from '@/components/forms/machines/Failures'
import { useForm } from 'react-hook-form'
import { addServerErrors } from '@/components/forms/common/FormBase'
import { formatDateOnly } from '@/common/time'
import { MachinePath } from './Common'
import { TMPGallery } from '@/components/Gallery/Gallery'
import { Roles } from '@/services/repository/users'
import { MachineStatuses } from './statuses'
import { CommunalLangsButton } from '@/components/layout/Langs'

const rolesAllowsFailureView = [Roles.CompanyAdmin, Roles.ServicemanRole, Roles.SuperAdmin];

export interface NewMachineFailurePopupProps {
  services: Services,
  session?: CurrentSession,
  translates: Translates,
  isOpen: boolean,
  machineId: string | undefined
  onClose: () => void
}

export const NewMachineFailurePopup = ({ services, session, onClose, machineId, translates }: NewMachineFailurePopupProps) => {
  const form = useForm<MachineFailureFormData>()
  const navigate = useNavigate()
  const [message, setMessage] = useState<string | undefined>(undefined)

  const handle = (data: MachineFailureFormData): Promise<void> => {
    if (form === undefined) {
      return Promise.reject()
    }
    delete data.id
    return services.session.fetchWithToken(`/api/public/machines/byid/${machineId}/failures`, {
      method: 'POST',
      body: JSON.stringify({
        ...data,
        machineFailureTypeId: parseInt(data.machineFailureTypeId as any)
      }),
    }).then(async (r) => {
      if (r.status !== 200) {
        const erros = await r.json()
        addServerErrors(form, translates, erros)
        return
      }
      form.reset()
    }).then(() => {
      if (session && rolesAllowsFailureView.includes(session?.user.role as Roles)) {
        return navigate(`/communal/machine/${machineId}/failures`)
      }
      setMessage(translates.for('page.machinefailures.thanks_for_report'))
      // onClose()
    })
  }

  return <div>
    {message === undefined ? <NewMachineFailureForm
      onSubmit={handle}
      services={services}
      form={form}
      translates={translates}
      formName='machine_failures'
      readOnly={false}
      session={session}
    /> : <div className='communal-dashboard-message-popup'>{message}</div>}
  </div>
}

export const CommunalMachineDashboard = ({ services, session }: { services: Services, session: CurrentSession | undefined }) => {
  const mid = useParams().mid
  const navigate = useNavigate()
  const { redirectToLogin } = useLogin()
  const [machine, setMachine] = useState<Machine>()
  const [translates, setTranslates] = useState<Translates>()
  const { openModal } = useModal()

  useEffect(() => {
    if (session) {
      setTranslates(session.translates)
      return
    }
    services.i18n.get('main').then(setTranslates)
  }, [session])

  useEffect(() => {
    fetch(`/api/communal/machines/byid/${mid}`).then((response) => {
      return response.json()
    }).then((data) => {
      setMachine(data)
    })
  }, [mid])

  if (translates === undefined) {
    return <div className='spinner'></div>
  }

  function forAnons() {
    if (translates === undefined) {
      return <div className='spinner'></div>
    }
    return null
    // return <div className='spaced-block'>
    //   <a className='main-red-btn' onClick={() => {
    //     redirectToLogin({
    //       redirectTo: `/communal/machine/${mid}`
    //     })
    //   }}>
    //     {translates.for('menus.communal.machine.login')}
    //   </a>
    // </div>
  }

  function forLoggedIn() {
    if (session === undefined) {
      return null
    }
    if (translates === undefined) {
      return <div className='spinner'></div>
    }
    return <div>
      {session?.user.role == Roles.SuperAdmin || session?.user.role == Roles.CompanyAdmin || session?.user.role == Roles.ServicemanRole ? <>
        <a className='main-red-btn' onClick={() => { navigate(`/communal/machine/${mid}/failures`) }}>
          {translates.for('menus.communal.machine.failure_list')}
        </a>
        {machine?.imei ? <a className='main-red-btn' onClick={() => { navigate(`/communal/machine/${mid}/hits`) }}>
          {translates.for('menus.communal.machine.hits_list')}
        </a> : null }
      </> : null}
      {/* <a className='main-btn' onClick={() => openModal((props) => MachinePopupQRCodeBody({ ...props, machineId: mid, services, session, translates }))}>
        {translates.for('menus.communal.machine.open_qrcode')}
      </a>
      <a className='main-btn' onClick={() => { navigate(`/communal/machine/${mid}/teltonika`) }}>
        {translates.for('menus.communal.machine.monitoring')}
      </a> */}
    </div>
  }

  if (machine === undefined || translates === undefined) {
    return <div className='spinner'></div>
  }

  return <MainPageLayout pageName='communal-dashboard' services={services} translates={translates} session={session} path={MachinePath({ session, translates, machine })}
    navbarLeft={session === undefined ? <LoginButton translates={translates} /> : <BackButton services={services} translates={translates} replaceUrl='/' />}
    navbarRight={<CommunalLangsButton session={session} services={services} translates={translates} />} >
    <div className="container">

      {machine.machineType?.manufacturer ? <div className='machine-logo-wrap'>
        <img src={`/public/img/machines/manufacture/${machine.machineType?.manufacturer}/logo.png`} />
      </div> : null}

      {machine.machineType?.model ? <div className='machine-type'>
        {translates.for(`machine.types.${machine.machineType?.category}`)}
      </div> : null}

      {machine.machineType?.model ? <div className='machine-type-row'>
        <span className='label'>{translates.for('forms.machine.fields.model.title')}: </span>
        <span className='value'>{machine.machineType?.model}</span>
      </div> : null}

      {machine.internalNumber ? <div className='machine-type-row'>
        <span className='label'>{translates.for('machine.internal_number')}: </span>
        <span className='value'>{machine.internalNumber}</span>
      </div> : null}

      {/* <div className='machine-name'>{machine.name}</div> */}
      {MachineStatuses(translates, machine)}

      <a className='main-gray-btn' onClick={() => { navigate(`/communal/machine/${mid}/details`) }}>
        {translates.for('menus.communal.machine.details')}
      </a>
      {session?.user.role == Roles.SuperAdmin || session?.user.role == Roles.CompanyAdmin || session?.user.role == Roles.ServicemanRole ? <a className='main-gray-btn' onClick={() => { navigate(`/communal/machine/${mid}/files`) }}>
        {translates.for('menus.communal.machine.files')}
      </a> : null}
      {/* <a className='main-btn' onClick={() => { openModal((props) => MachinePopupDocBody({ ...props, machineTypeId: machine.machineTypeId, session, services, translates })) }}>
        <i className='svgfont-documentation' />
        {translates.for('menus.communal.machine.documentation')}
      </a> */}
      {/* <a className='main-btn' onClick={() => openModal((props) => NewMachineFailurePopup({ ...props, services, session, translates, machineId: mid }))}>
        {translates.for('menus.communal.machine.failure')}
      </a> */}
      <a className='main-red-btn' onClick={() => { navigate(`/communal/machine/${mid}/failures/add`) }}>
        {translates.for('menus.communal.machine.failure')}
      </a>
      {/* { session === undefined ? <a className='main-red-btn' onClick={() => { navigate(`/login`) }}>
        {translates.for('menus.communal.machine.login')}
      </a> : null } */}

      {session === undefined ? forAnons() : forLoggedIn()}

      <a className='main-btn' onClick={() => { navigate(`/communal/machine/${mid}/rent`) }}>
        {translates.for('menus.communal.machine.hire_me')}
      </a>
      <a className='main-btn' onClick={() => { navigate(`/communal/machine/${mid}/buy`) }}>
        {translates.for('menus.communal.machine.buy_me')}
      </a>
      <a className='main-btn' onClick={() => { navigate(`/communal/contact`) }}>
        {translates.for('menus.communal.machine.call')}
      </a>
      <a className='main-green-btn' onClick={() => { navigate(`/communal/support/wizard`) }}>
        {translates.for('menus.communal.machine.support_page')}
      </a>

      {/* {callMeBtn({ services, translates })} */}
    </div>
  </MainPageLayout>
}
